import React from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Heading,
  Text,
  Flex,
  Icon,
  VStack,
} from '@chakra-ui/react';
import { FaClock, FaChartLine, FaHouseUser, FaCalendarCheck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionBox = motion(Box);

const StatsCard = ({ title, stat, icon, description }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Stat
      px={{ base: 4, md: 8 }}
      py={'5'}
      shadow={'xl'}
      border={'1px solid'}
      borderColor={useColorModeValue('gray.100', 'gray.700')}
      rounded={'2xl'}
      bg={useColorModeValue('white', 'gray.800')}
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: 'xl',
      }}
    >
      <VStack spacing={3} align="flex-start">
        <Flex alignItems="center" justifyContent="space-between" w="full">
          <Box
            color={useColorModeValue('blue.500', 'blue.300')}
            bg={useColorModeValue('blue.50', 'blue.900')}
            rounded="full"
            p={2}
          >
            {icon}
          </Box>
          <StatNumber fontSize={'3xl'} fontWeight={'bold'}>
            {stat}
          </StatNumber>
        </Flex>
        <StatLabel fontWeight={'medium'} fontSize="lg" isTruncated>
          {title}
        </StatLabel>
        <StatHelpText fontSize={'sm'} color={useColorModeValue('gray.600', 'gray.400')}>
          {description}
        </StatHelpText>
      </VStack>
    </Stat>
  </MotionBox>
);

export default function Statistics() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <Box
      as="section"
      id="statistics"
      py={20}
      bg={useColorModeValue('gray.50', 'gray.900')}
      ref={ref}
    >
      <Container maxW="7xl">
        <VStack spacing={8} as={MotionBox}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Heading
            as="h2"
            fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
            fontWeight="bold"
            textAlign={'center'}
            color={useColorModeValue('gray.900', 'white')}
          >
            Ihre Arbeitszeit auf einen Blick
          </Heading>
          <Text fontSize={'xl'} color={useColorModeValue('gray.600', 'gray.300')} textAlign={'center'} maxW="3xl">
            Erhalten Sie detaillierte Einblicke in Ihre Arbeitszeiten und steigern Sie Ihre Produktivität mit unserer innovativen Zeiterfassung.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={{ base: 8, lg: 12 }} mt={16}>
          <StatsCard
            title={'Gesamtarbeitszeit'}
            stat={'160h'}
            icon={<Icon as={FaClock} w={6} h={6} />}
            description={'Dieser Monat'}
          />
          <StatsCard
            title={'Durchschnitt/Tag'}
            stat={'8h'}
            icon={<Icon as={FaChartLine} w={6} h={6} />}
            description={'Basierend auf Werktagen'}
          />
          <StatsCard
            title={'Überstunden'}
            stat={'+5h'}
            icon={<Icon as={FaCalendarCheck} w={6} h={6} />}
            description={'Kumuliert'}
          />
          <StatsCard
            title={'Home-Office Tage'}
            stat={'8'}
            icon={<Icon as={FaHouseUser} w={6} h={6} />}
            description={'Dieser Monat'}
          />
        </SimpleGrid>
      </Container>
    </Box>
  );
}
